'use client';
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { getAssetContentUrl, getAssetIcon, getHumanizedAssetDisplayName, } from '#pplx/frontend/apps/ask/asset-utils/assetHelpers.js';
import { isExternalUrlAsset } from '#pplx/frontend/apps/ask/asset-utils/externalUrlAsset.js';
import { pinnedArtifactMenuMessages } from '#pplx/frontend/apps/ask/hooks/sidebar-pinned-actions/useSidebarPinnedArtifactInteractions.js';
import { Item } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/Item.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
const LazySidebarArtifactHoverPreview = Dynamic(async () => {
    const { SidebarArtifactHoverPreview } = await import('#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/SidebarArtifactHoverPreview.js');
    return { default: SidebarArtifactHoverPreview };
}, { loading: ({ children }) => _jsx(_Fragment, { children: children }) });
export const PinnedArtifactItem = memo(function PinnedArtifactItem({ asset, position, isSidebarOpen, isInPopover, collapsed, foreground, onUnpin, interactionsFor, unpinLabel, fallbackTitle, showTypeIcon, }) {
    const pathname = usePathname();
    const [menuIsActive, setMenuIsActive] = useState(false);
    const link = useMemo(() => getAssetContentUrl(asset), [asset]);
    const title = useMemo(() => getHumanizedAssetDisplayName(asset, fallbackTitle), [asset, fallbackTitle]);
    const handleUnpin = useCallback(() => onUnpin(asset), [asset, onUnpin]);
    const interactions = useMemo(() => interactionsFor({
        asset,
        title,
        position,
        placement: isInPopover === true ? 'collapsedPopover' : 'sidebar',
    }), [asset, interactionsFor, isInPopover, position, title]);
    const { openSession } = interactions;
    // Click handlers receive the DOM event, which must not reach open's track option.
    const handleOpen = useCallback(() => interactions.open(), [interactions]);
    const { $t } = useIntl();
    const menuItems = useMemo(() => {
        const items = [
            {
                text: unpinLabel,
                icon: $icon('pinned-off'),
                onClick: handleUnpin,
            },
        ];
        switch (openSession.kind) {
            case 'available': {
                const isComputerTask = openSession.threadType === 'computer';
                items.push({
                    text: $t(isComputerTask
                        ? pinnedArtifactMenuMessages.openComputerSession
                        : pinnedArtifactMenuMessages.openSearchSession),
                    icon: isComputerTask ? $icon('custom-computer') : $icon('custom-perplexity-thread'),
                    onClick: openSession.onSelect,
                });
                break;
            }
            case 'unavailable':
                break;
            default:
                assertUnreachableValue(openSession);
        }
        return items;
    }, [handleUnpin, openSession, unpinLabel, $t]);
    const itemElement = (_jsx(Item, { variant: "secondary", href: link, onClick: handleOpen, text: title, sidebarOpen: isSidebarOpen, isInPopover: isInPopover, selected: pathname === link, menuLabel: $t(pinnedArtifactMenuMessages.actions), menuItems: menuItems, onMenuOpenChange: setMenuIsActive, collapsed: collapsed, foreground: foreground, accessory: showTypeIcon ? (_jsx("span", { className: "inline-flex opacity-50", children: _jsx(Icon, { icon: getAssetIcon(asset), size: "small", color: "ultraLight", "aria-hidden": true }) })) : undefined }));
    if (!isSidebarOpen || isInPopover || isExternalUrlAsset(asset))
        return itemElement;
    return (_jsx(LazySidebarArtifactHoverPreview, { asset: asset, displayName: title, forceClosed: menuIsActive, onContentClick: handleOpen, children: itemElement }));
});
//# sourceMappingURL=ArtifactItems.js.map